import React, { Fragment, useState } from 'react';
import {
    Avatar,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Skeleton,
    Tab,
    Tabs,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiArrowRight,
    mdiClipboardPulse,
    mdiTrendingDown,
    mdiTrendingNeutral,
    mdiTrendingUp,
} from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import { ApiStatus } from '../../utils/enums';
import { formatNumber, formatNumberPercent } from '../../utils/formattingUtils';
import GradeLabel from '../Labels/GradeLabel';

const classesPrefix = 'networkHealthImpactCard';

const classes = {
    cardHeaderContent: `${classesPrefix}-cardHeaderContent`,
    cardContent: `${classesPrefix}-cardContent`,
    impactCard: `${classesPrefix}-impactCard`,
    loader: `${classesPrefix}-loader`,
    impactValueWrapper: `${classesPrefix}-impactValueWrapper`,
    impactComparisonIcon: `${classesPrefix}-impactComparisonIcon`,
    impactLabel: `${classesPrefix}-impactLabel`,
    changeIndicator: `${classesPrefix}-changeIndicator`,
    changeIndicatorIcon: `${classesPrefix}-changeIndicatorIcon`,
    goodChangeIndicator: `${classesPrefix}-goodChangeIndicator`,
    badChangeIndicator: `${classesPrefix}-badChangeIndicator`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledCard = styled(Card)(({ theme }) => {
    return {
        [`& .${classes.cardHeaderContent}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        [`& .${classes.cardContent}`]: {
            paddingTop: theme.spacing(0),
        },
        [`& .${classes.impactCard}`]: {
            textAlign: 'center',
        },
        [`& .${classes.loader}`]: {
            display: 'inline-block',
        },
        [`& .${classes.impactValueWrapper}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        [`& .${classes.impactComparisonIcon}`]: {
            margin: theme.spacing(0, 0.5),
            color: theme.palette.text.secondary,
        },
        [`& .${classes.impactLabel}`]: {
            marginBottom: theme.spacing(3),
        },
        [`& .${classes.changeIndicator}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        [`& .${classes.changeIndicatorIcon}`]: {
            marginTop: theme.spacing(0.5),
        },
        [`& .${classes.goodChangeIndicator}`]: {
            color: theme.palette.success.main,
        },
        [`& .${classes.badChangeIndicator}`]: {
            color: theme.palette.error.main,
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

enum LoadHoursTabs {
    Shipper = 'Shipper',
    Consignee = 'Consignee',
}

const NetworkHealthImpactCard = (): React.JSX.Element => {
    const [selectedTab, setSelectedTab] = useState(LoadHoursTabs.Shipper);

    const networkRecommendationsApiStatus = useAppSelector((state) => { return state.networkRx.networkRecommendationsApiStatus; });
    const networkRecommendations = useAppSelector((state) => { return state.networkRx.networkRecommendations; });

    const selectedRecommendations = useAppSelector((state) => { return state.networkRx.selectedRecommendations; });

    const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newTab: LoadHoursTabs): void => {
        setSelectedTab(newTab);
    };

    const isLoading = networkRecommendationsApiStatus === ApiStatus.Idle || networkRecommendationsApiStatus === ApiStatus.Loading;

    const renderChangeIndicator = (before: number | null, after: number | null, goodChange: 'higher' | 'lower'): React.JSX.Element => {
        if (after !== null && before) {
            const change = after - before;
            const percentChange = (change / before) * 100;

            let className = '';
            let icon = mdiTrendingNeutral;
            // change is 0, no change
            // lower is better, negative change is good OR higher is better, positive change is good
            if (change === 0) {
                icon = mdiTrendingNeutral; // flat
                className = '';
            } else if ((goodChange === 'lower' && change < 0) || (goodChange === 'higher' && change > 0)) {
                icon = mdiTrendingUp; // up
                className = classes.goodChangeIndicator;
            } else {
                icon = mdiTrendingDown; // down
                className = classes.badChangeIndicator;
            }

            return (
                <Typography className={`${classes.changeIndicator} ${className}`} variant='body2'>
                    <span><Icon className={classes.changeIndicatorIcon} path={icon} size={0.875} /></span>
                    <span>{formatNumberPercent(percentChange, 2, 2)}</span>
                </Typography>
            );
        }

        return <Typography variant='body2'>N/A</Typography>;
    };

    return (
        <StyledCard>
            <CardHeader
                classes={{
                    content: classes.cardHeaderContent,
                }}
                avatar={
                    <Avatar>
                        <Icon path={mdiClipboardPulse} size={1} />
                    </Avatar>
                }
                title='Network Health Impact'
                titleTypographyProps={{
                    variant: 'h5',
                }}
            />
            <CardContent className={classes.cardContent}>
                <Grid
                    container
                    spacing={1}
                    className={classes.impactCard}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Grid item xs={12} md={1} lg={2}>
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span>The </span>
                                    <span className={classes.title}>Overall Grade</span>
                                    <span> for the network is an average of the Health Assessment grades for area, shipper, and consignee metrics.</span>
                                </Typography>
                            }
                        >
                            <div className={classes.impactValueWrapper}>
                                <GradeLabel
                                    apiStatus={networkRecommendationsApiStatus}
                                    grade={networkRecommendations.network_health.network_grade}
                                    variant='h1'
                                />
                                {
                                    selectedRecommendations.length > 0 &&
                                    <Fragment>
                                        <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                        <GradeLabel
                                            apiStatus={networkRecommendationsApiStatus}
                                            grade={selectedRecommendations[0].network_health_impact.network_grade}
                                            variant='h1'
                                        />
                                    </Fragment>
                                }
                            </div>
                        </StyledTooltip>
                        <Typography
                            variant='h6'
                            gutterBottom
                            sx={{
                                color: 'textSecondary',
                            }}
                        >
                            Network Grade
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={11} lg={10}>
                        <Grid
                            container
                            spacing={1}
                            sx={{
                                alignItems: 'flex-end',
                            }}
                        >
                            <Grid item xs={12} sm={6} md={4}>
                                {
                                    selectedRecommendations.length > 0 &&
                                    renderChangeIndicator(
                                        networkRecommendations.network_health.network_stats.average_daily_balance_amount,
                                        selectedRecommendations[0].network_health_impact.network_stats.average_daily_balance_amount,
                                        'lower'
                                    )
                                }
                                <StyledTooltip
                                    title={
                                        <Typography variant='body2' className={classes.helpText}>
                                            <span className={classes.title}>Balance</span>
                                            <span> is the difference between daily inbound and outbound orders for all areas in the network and assumes 0 is ideal.</span>
                                        </Typography>
                                    }
                                >
                                    <div className={classes.impactValueWrapper}>
                                        <Typography variant='h4'>
                                            {
                                                isLoading ?
                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                    formatNumber(networkRecommendations.network_health.network_stats.average_daily_balance_amount, 2, 2)
                                            }
                                        </Typography>

                                        {
                                            selectedRecommendations.length > 0 &&
                                            <Fragment>
                                                <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumber(selectedRecommendations[0].network_health_impact.network_stats.average_daily_balance_amount, 2, 2)
                                                    }
                                                </Typography>
                                            </Fragment>
                                        }
                                    </div>
                                </StyledTooltip>
                                <Typography
                                    variant='h6'
                                    className={classes.impactLabel}
                                    sx={{
                                        color: 'textSecondary',
                                    }}
                                >
                                    Avg. Daily Balance
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                {
                                    selectedRecommendations.length > 0 &&
                                    renderChangeIndicator(
                                        networkRecommendations.network_health.network_stats.average_netwise_in_transit_delay_hours_ratio,
                                        selectedRecommendations[0].network_health_impact.network_stats.average_netwise_in_transit_delay_hours_ratio,
                                        'lower'
                                    )
                                }
                                <StyledTooltip
                                    title={
                                        <Typography variant='body2' className={classes.helpText}>
                                            <span className={classes.title}>Average In Transit Delay</span>
                                            <span> compares actual transit time to a standard transit time governed by Hours of Service (HOS) rules. A smaller delay is desired.</span>
                                        </Typography>
                                    }
                                >
                                    <div className={classes.impactValueWrapper}>
                                        <Typography variant='h4'>
                                            {
                                                isLoading ?
                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                    formatNumber(networkRecommendations.network_health.network_stats.average_netwise_in_transit_delay_hours_ratio, 2, 2)
                                            }
                                        </Typography>

                                        {
                                            selectedRecommendations.length > 0 &&
                                            <Fragment>
                                                <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumber(selectedRecommendations[0].network_health_impact.network_stats.average_netwise_in_transit_delay_hours_ratio, 2, 2)
                                                    }
                                                </Typography>
                                            </Fragment>
                                        }
                                    </div>
                                </StyledTooltip>
                                <Typography
                                    variant='h6'
                                    className={classes.impactLabel}
                                    sx={{
                                        color: 'textSecondary',
                                    }}
                                >
                                    Avg. In Transit Delay
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                {
                                    selectedRecommendations.length > 0 &&
                                    renderChangeIndicator(
                                        networkRecommendations.network_health.network_stats.average_scheduled_vs_actual_miles_per_hour,
                                        selectedRecommendations[0].network_health_impact.network_stats.average_scheduled_vs_actual_miles_per_hour,
                                        'higher'
                                    )
                                }
                                <StyledTooltip
                                    title={
                                        <Typography variant='body2' className={classes.helpText}>
                                            <span className={classes.title}>Scheduled vs Actual Miles Per Hour</span>
                                            <span> evaluates the difference between Scheduled MPH and Actual MPH where a quotient of 1 is presumed to be delivering on time.</span>
                                        </Typography>
                                    }
                                >
                                    <div className={classes.impactValueWrapper}>
                                        <Typography variant='h4'>
                                            {
                                                isLoading ?
                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                    formatNumber(networkRecommendations.network_health.network_stats.average_scheduled_vs_actual_miles_per_hour, 2, 2)
                                            }
                                        </Typography>

                                        {
                                            selectedRecommendations.length > 0 &&
                                            <Fragment>
                                                <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumber(selectedRecommendations[0].network_health_impact.network_stats.average_scheduled_vs_actual_miles_per_hour, 2, 2)
                                                    }
                                                </Typography>
                                            </Fragment>
                                        }
                                    </div>
                                </StyledTooltip>
                                <Typography
                                    variant='h6'
                                    className={classes.impactLabel}
                                    sx={{
                                        color: 'textSecondary',
                                    }}
                                >
                                    Sch. vs Act. MPH
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                {
                                    selectedRecommendations.length > 0 &&
                                    renderChangeIndicator(
                                        networkRecommendations.network_health.network_stats.average_daily_density_amount,
                                        selectedRecommendations[0].network_health_impact.network_stats.average_daily_density_amount,
                                        'higher'
                                    )
                                }
                                <StyledTooltip
                                    title={
                                        <Typography variant='body2' className={classes.helpText}>
                                            <span className={classes.title}>Density</span>
                                            <span> is the average count of daily inbound orders for all areas in the network. Higher density is desired.</span>
                                        </Typography>
                                    }
                                >
                                    <div className={classes.impactValueWrapper}>
                                        <Typography variant='h4'>
                                            {
                                                isLoading ?
                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                    formatNumber(networkRecommendations.network_health.network_stats.average_daily_density_amount, 2, 2)
                                            }
                                        </Typography>

                                        {
                                            selectedRecommendations.length > 0 &&
                                            <Fragment>
                                                <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumber(selectedRecommendations[0].network_health_impact.network_stats.average_daily_density_amount, 2, 2)
                                                    }
                                                </Typography>
                                            </Fragment>
                                        }
                                    </div>
                                </StyledTooltip>
                                <Typography
                                    variant='h6'
                                    className={classes.impactLabel}
                                    sx={{
                                        color: 'textSecondary',
                                    }}
                                >
                                    Avg. Daily Density
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                {
                                    selectedRecommendations.length > 0 &&
                                    renderChangeIndicator(
                                        networkRecommendations.network_health.network_stats.empty_miles_percentage_amount,
                                        selectedRecommendations[0].network_health_impact.network_stats.empty_miles_percentage_amount,
                                        'lower'
                                    )
                                }
                                <StyledTooltip
                                    title={
                                        <Typography variant='body2' className={classes.helpText}>
                                            <span className={classes.title}>Empty Mile %</span>
                                            <span> is the quotient of order empty miles and order total miles expressed as a percentage. Lower empty miles are desired.</span>
                                        </Typography>
                                    }
                                >
                                    <div className={classes.impactValueWrapper}>
                                        <Typography variant='h4'>
                                            {
                                                isLoading ?
                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                    formatNumberPercent(networkRecommendations.network_health.network_stats.empty_miles_percentage_amount, 2, 2)
                                            }
                                        </Typography>

                                        {
                                            selectedRecommendations.length > 0 &&
                                            <Fragment>
                                                <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumberPercent(selectedRecommendations[0].network_health_impact.network_stats.empty_miles_percentage_amount, 2, 2)
                                                    }
                                                </Typography>
                                            </Fragment>
                                        }
                                    </div>
                                </StyledTooltip>
                                <Typography
                                    variant='h6'
                                    className={classes.impactLabel}
                                    sx={{
                                        color: 'textSecondary',
                                    }}
                                >
                                    Empty Miles %
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Tabs
                                    indicatorColor='primary'
                                    centered
                                    value={selectedTab}
                                    onChange={handleTabChange}
                                >
                                    <Tab value={LoadHoursTabs.Shipper} label='Shipper' />
                                    <Tab value={LoadHoursTabs.Consignee} label='Consignee' />
                                </Tabs>
                                {
                                    selectedTab === LoadHoursTabs.Shipper &&
                                    <Fragment>
                                        {
                                            selectedRecommendations.length > 0 &&
                                            renderChangeIndicator(
                                                networkRecommendations.network_health.network_stats.average_load_hours_duration,
                                                selectedRecommendations[0].network_health_impact.network_stats.average_load_hours_duration,
                                                'lower'
                                            )
                                        }
                                        <StyledTooltip
                                            title={
                                                <Typography variant='body2' className={classes.helpText}>
                                                    <span className={classes.title}>Load Time</span>
                                                    <span> is the amount of hours spent at a pickup location. Fewer hours are desired.</span>
                                                </Typography>
                                            }
                                        >
                                            <div className={classes.impactValueWrapper}>
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumber(networkRecommendations.network_health.network_stats.average_load_hours_duration, 2, 2)
                                                    }
                                                </Typography>

                                                {
                                                    selectedRecommendations.length > 0 &&
                                                    <Fragment>
                                                        <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                        <Typography variant='h4'>
                                                            {
                                                                isLoading ?
                                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                                    formatNumber(selectedRecommendations[0].network_health_impact.network_stats.average_load_hours_duration, 2, 2)
                                                            }
                                                        </Typography>
                                                    </Fragment>
                                                }
                                            </div>
                                        </StyledTooltip>
                                        <Typography
                                            variant='h6'
                                            gutterBottom
                                            sx={{
                                                color: 'textSecondary',
                                            }}
                                        >
                                            Avg. Load Time
                                        </Typography>
                                    </Fragment>
                                }
                                {
                                    selectedTab === LoadHoursTabs.Consignee &&
                                    <Fragment>
                                        {
                                            selectedRecommendations.length > 0 &&
                                            renderChangeIndicator(
                                                networkRecommendations.network_health.network_stats.average_unload_hours_duration,
                                                selectedRecommendations[0].network_health_impact.network_stats.average_unload_hours_duration,
                                                'lower'
                                            )
                                        }
                                        <StyledTooltip
                                            title={
                                                <Typography variant='body2' className={classes.helpText}>
                                                    <span className={classes.title}>Unload Time</span>
                                                    <span> is the amount of hours spent at a delivery location. Fewer hours are desired.</span>
                                                </Typography>
                                            }
                                        >
                                            <div className={classes.impactValueWrapper}>
                                                <Typography variant='h4'>
                                                    {
                                                        isLoading ?
                                                            <Skeleton width='100px' className={classes.loader} /> :
                                                            formatNumber(networkRecommendations.network_health.network_stats.average_unload_hours_duration, 2, 2)
                                                    }
                                                </Typography>

                                                {
                                                    selectedRecommendations.length > 0 &&
                                                    <Fragment>
                                                        <Icon className={classes.impactComparisonIcon} path={mdiArrowRight} size={1} />
                                                        <Typography variant='h4'>
                                                            {
                                                                isLoading ?
                                                                    <Skeleton width='100px' className={classes.loader} /> :
                                                                    formatNumber(selectedRecommendations[0].network_health_impact.network_stats.average_unload_hours_duration, 2, 2)
                                                            }
                                                        </Typography>
                                                    </Fragment>
                                                }
                                            </div>
                                        </StyledTooltip>
                                        <Typography
                                            variant='h6'
                                            gutterBottom
                                            sx={{
                                                color: 'textSecondary',
                                            }}
                                        >
                                            Avg. Unload Time
                                        </Typography>
                                    </Fragment>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </StyledCard>

    );
};

export default NetworkHealthImpactCard;
